"use client";
import { useState } from 'react';
import { Calendar, Clock, User, Shield, CheckCircle2 } from 'lucide-react';

export default function ConsultationForm({ expertName = '', expertRole = '' }: { expertName?: string; expertRole?: string }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({ topic: '', date: '', time: '', desc: '', name: '', phone: '' });

  // Dynamiczne usługi na podstawie roli
  const getServices = () => {
    switch(expertRole) {
      case 'Adwokat': return ['Rozwód z orzekaniem o winie', 'Ustalenie alimentów / opieki', 'Podział majątku', 'Interwencja prawna / Pismo'];
      case 'Psycholog': return ['Wsparcie w kryzysie rozwodowym', 'Komunikacja z narcyzem', 'Pomoc psychologiczna dla dzieci', 'Terapia indywidualna'];
      case 'Detektyw': return ['Obserwacja i dokumentacja zdrady', 'Zbieranie dowodów do sprawy', 'Ustalanie ukrytego majątku', 'Wywiad środowiskowy'];
      default: return ['Konsultacja wstępna', 'Inna sprawa'];
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep(4);
  };

  return (
    <div className="bg-[#0A101D] border-t-4 border-[#D6B570] border-x border-b border-white/10 rounded-2xl p-6 md:p-10 shadow-2xl relative overflow-hidden">
      <div className="absolute top-0 right-0 p-8 opacity-5 text-white">
        <Shield size={120} />
      </div>
      <div className="flex items-center justify-between mb-8 relative z-10">
        <h3 className="text-2xl font-bold text-white">Zgłoś sprawę</h3>
        <span className="text-sm font-semibold text-[#D6B570]">Krok {step} z 3</span>
      </div>

      {step === 1 && (
        <div className="space-y-6 relative z-10 animate-in fade-in">
          <p className="font-medium text-gray-300">1. Wybierz rodzaj usługi ({expertRole})</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {getServices().map(topic => (
              <button 
                key={topic}
                type="button"
                onClick={() => { setFormData({...formData, topic}); setStep(2); }}
                className="p-4 border border-white/10 bg-[#070B14] rounded-xl text-left hover:border-[#D6B570] hover:bg-white/5 transition-all text-sm font-medium text-gray-200 cursor-pointer"
              >
                {topic}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6 relative z-10 animate-in fade-in">
          <p className="font-medium text-gray-300">2. Krótko opisz sytuację i proponowany termin</p>
          <textarea 
            rows={3} 
            placeholder="Na czym najbardziej Ci zależy? Z czym masz problem?" 
            className="w-full p-4 border border-white/10 rounded-xl bg-[#070B14] focus:ring-2 focus:ring-[#D6B570] outline-none resize-none text-sm text-white placeholder-gray-500"
            onChange={(e) => setFormData({...formData, desc: e.target.value})}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-400 uppercase"><Calendar size={14} className="inline mr-1 text-[#D6B570]"/> Data</label>
              <input type="date" className="w-full p-3 border border-white/10 rounded-xl bg-[#070B14] focus:ring-2 focus:ring-[#D6B570] outline-none text-sm text-white" onChange={(e) => setFormData({...formData, date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-400 uppercase"><Clock size={14} className="inline mr-1 text-[#D6B570]"/> Godzina</label>
              <select className="w-full p-3 border border-white/10 rounded-xl bg-[#070B14] focus:ring-2 focus:ring-[#D6B570] outline-none text-sm text-white cursor-pointer" onChange={(e) => setFormData({...formData, time: e.target.value})}>
                <option value="">Wybierz...</option>
                <option value="Rano">Rano (09:00 - 12:00)</option>
                <option value="Południe">Południe (12:00 - 16:00)</option>
                <option value="Wieczór">Wieczór (16:00 - 19:00)</option>
              </select>
            </div>
          </div>
          <div className="pt-2 flex justify-between">
            <button type="button" onClick={() => setStep(1)} className="text-sm text-gray-400 hover:text-white cursor-pointer">Wróć</button>
            <button type="button" onClick={() => setStep(3)} disabled={!formData.date} className="px-6 py-2 bg-[#D6B570] text-[#070B14] rounded-lg font-bold disabled:opacity-50 hover:bg-[#ebd097] transition-colors cursor-pointer">Dalej</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <form onSubmit={handleSubmit} className="space-y-6 relative z-10 animate-in fade-in">
          <p className="font-medium text-gray-300">3. Dyskretne dane kontaktowe</p>
          <div className="space-y-4">
            <div className="flex border border-white/10 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-[#D6B570] bg-[#070B14]">
              <span className="p-3 text-gray-400 border-r border-white/10"><User size={20}/></span>
              <input required type="text" placeholder="Imię lub pseudonim" className="w-full p-3 bg-transparent outline-none text-white placeholder-gray-500" onChange={(e) => setFormData({...formData, name: e.target.value})} />
            </div>
            <div className="flex border border-white/10 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-[#D6B570] bg-[#070B14]">
              <span className="p-3 text-gray-400 border-r border-white/10"><Shield size={20}/></span>
              <input required type="tel" placeholder="Bezpieczny numer telefonu" className="w-full p-3 bg-transparent outline-none text-white placeholder-gray-500" onChange={(e) => setFormData({...formData, phone: e.target.value})} />
            </div>
          </div>
          <div className="pt-2 flex justify-between items-center">
            <button type="button" onClick={() => setStep(2)} className="text-sm text-gray-400 hover:text-white cursor-pointer">Wróć</button>
            <button type="submit" className="px-8 py-3 bg-[#D6B570] text-[#070B14] rounded-lg font-bold shadow-lg hover:shadow-xl hover:bg-[#ebd097] transition-all cursor-pointer">Wyślij zgłoszenie</button>
          </div>
        </form>
      )}

      {step === 4 && (
        <div className="text-center space-y-4 py-8 relative z-10 animate-in zoom-in">
          <CheckCircle2 size={64} className="mx-auto text-[#D6B570]" />
          <h4 className="text-xl font-bold text-white">Zgłoszenie zabezpieczone</h4>
          <p className="text-sm text-gray-400">Oczekuj na bezpieczny kontakt z naszej strony. Działamy w pełnej dyskrecji.</p>
        </div>
      )}
    </div>
  );
}
